import * as React from 'react'
import { useState, useEffect, useCallback } from 'react'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { observer } from 'mobx-react-lite'
// evertel
import { useService } from '@evertel/di'
import { Modal, ModalBody, ModalHeader, ModalFooter, Row, Col, InfoBox, Text, FormInput, Button, PasswordInput } from '@evertel/web/ui'
import { AuthenticationController } from '@evertel/shared/feature-authentication-auth0'
import { Analytics } from '@evertel/analytics'
import { SessionService } from '@evertel/session/SessionService'
import { DeviceTokenService } from '@evertel/session/DeviceTokenService'
import { CurrentUserController } from '@evertel/blue-user'

interface Props {
    isOpen: boolean
    onClose: () => void
    onCodeRequested: (phoneNumber: string) => void
}

const Enroll2FAModal: React.FC<Props> = observer(({
    isOpen = false,
    onClose,
    onCodeRequested
}) => {

    const analytics = useService(Analytics)
    const sessionService = useService(SessionService)
    const currentUserController = useService(CurrentUserController, [sessionService.currentUserId])
    const deviceTokenService = useService(DeviceTokenService)
    const authenticationController = useService(AuthenticationController)

    const existingPhoneParsed = currentUserController.user?.phoneNumber
        ? parsePhoneNumberFromString(String(currentUserController.user?.phoneNumber), 'US')?.formatNational()
        : ''

    const [error, setError] = useState('')
    const [phoneNumber, setPhoneNumber] = useState(existingPhoneParsed)
    const [password, setPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (isOpen) {
            // reset state
            setError('')
            setPhoneNumber(existingPhoneParsed)
            setPassword('')
            setIsLoading(false)

            analytics.logView('/account/security/enroll-mfa', true)
        }
    }, [isOpen])

    const submitEnrollMfa = useCallback(async () => {
        setError('')

        if (!password?.trim() || !phoneNumber?.trim()) {
            setError('Please fill out all fields')
            return
        }

        const parsedNumber = parsePhoneNumberFromString(phoneNumber, 'US')

        if (!parsedNumber?.isValid()) {
            setError('Invalid phone number')
            return
        }

        setIsLoading(true)

        const number = parsedNumber.number

        // Save phone to profile if they don't have one
        if (!currentUserController.user.phoneNumber) {
            await currentUserController.update({
                phoneNumber: number
            })
        }

        try {
            const body = {
                username: currentUserController.user.email,
                password: password.trim(),
                phoneNumber: number,
                deviceToken: deviceTokenService.token
            }

            const response = await authenticationController.enrollMFA(body)
            authenticationController.setMFAParams(response.access_token, response.oob_code)

            onCodeRequested(number as string)
        } catch (err: any) {
            setError(authenticationController.formatEnrollmentError(err))
        } finally {
            setIsLoading(false)
        }
    }, [password, phoneNumber, currentUserController, deviceTokenService, authenticationController, onCodeRequested])

    return (
        <Modal
            visible={isOpen}
            className="join-dept-modal"
            onClose={onClose}
        >
            <ModalHeader title="Enroll in SMS Authentication" />
            <ModalBody>
                <Row>
                    <Col>
                        <Text>
                            To enroll in SMS authentication, please enter your mobile number and account password below.
                        </Text>
                        {error && (
                            <InfoBox className="mt-3" color="danger">
                                {error}
                            </InfoBox>
                        )}
                        <FormInput
                            className="mb-4 mt-3"
                            label="Mobile Number"
                            type="text"
                            name="phoneNumber"
                            autoComplete="off"
                            placeholder="Type your mobile phone number"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            required={true}
                            leftIcon="phone"
                        />
                        <PasswordInput
                            label="Current Password"
                            value={password}
                            onChange={setPassword}
                            disabled={isLoading}
                            placeholder="Type your current password"
                            leftIcon="key"
                        />
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter className="text-center">
                <Row align="right">
                    <Col>
                        <Button
                            color="primary"
                            onClick={submitEnrollMfa}
                            disabled={isLoading}
                            loading={isLoading}>
                            <Text>Enroll</Text>
                        </Button>
                    </Col>
                </Row>
            </ModalFooter>
        </Modal>
    )
})

export {
    Enroll2FAModal
}
